import { AnimatePresence, motion } from 'framer-motion';

import type { FormProps, SanityDefaultForm } from '#/lib/sanity/queries/form';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';
import { Form } from '#/components/Form/Single';

import { addBusinessEmailValidation } from '../utils';
import { FormConfirmation } from './Confirmation';
import { FormErrorMessage } from './ErrorMessage';
import { FormFields } from './FormFields';

export function FormCustomSingle(props: FormProps<SanityDefaultForm>) {
  const formFieldsSchema = addBusinessEmailValidation(props.form);

  return (
    <div className={cn('relative', props.className)}>
      <Form
        pardotCampaignId={props.form.pardotCampaignId}
        pardotListIds={props.form.pardotListIds}
        formFieldsSchema={formFieldsSchema}
        trackingEventName={props.form.trackingEventName}
        trackingEventProperties={props.form.trackingEventProperties}
        enableScheduling={props.form.enableScheduling}
        onSubmitSuccess={props.onSubmitSuccess}
        onSubmitError={props.onSubmitError}
        ConfirmationComponent={({ isWaitingOnSchedulingLoad }) => (
          <FormConfirmation
            isWaitingOnSchedulingLoad={isWaitingOnSchedulingLoad}
            confirmation={props.confirmation}
          />
        )}
      >
        {({ control, formState }) => {
          return (
            <div className="grid grid-cols-2 gap-20">
              <FormFields fields={props.form.fields} control={control} />
              <FormErrorMessage
                formState={formState}
                className="col-span-full"
              />
              <Button className="col-span-full" variant="form" type="submit">
                <AnimatePresence mode="popLayout">
                  {formState.isSubmitting ? (
                    <motion.div
                      key="spinner"
                      className="mx-auto size-14 animate-spin rounded-full border-2 border-r-transparent"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <p className="sr-only">Submitting form…</p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {props.form.submitButtonLabel}
                    </motion.div>
                  )}
                </AnimatePresence>
              </Button>
              {props.form.legalText ? (
                <BasicText
                  className="text-legal col-span-full mt-10 font-light text-grayscale-05"
                  value={props.form.legalText}
                />
              ) : null}
            </div>
          );
        }}
      </Form>
    </div>
  );
}
